import {getGridDimensionsFromGQLCache} from 'inventory/storable-validation/InventoryTransferValidationFunctions';
import InventoryStructuredTableMode from 'notebook/integrations/samples/InventoryStructuredTableMode';
import {getColumnDefinitionIndex} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {CellErrorPosition} from 'notebook/structured-tables/StructuredTableConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {BaseTypedValueType, Table, TableColumnType} from 'notebook/tables/TableConstants';
import {TransferTableColumnGroup} from 'notebook/transfer-tables/TransferTableConstants';
import {getAlphanumericPositionFromWellIndex, isIntegerPosition} from 'samples/SampleUtil';
import {assertNever} from 'util/Types';

import {
  BARCODE_ALREADY_ENTERED_ERROR,
  CONTAINER_ALREADY_ENTERED_ERROR,
  POSITION_ALREADY_ENTERED_ERROR,
  TransferStructuredTable,
  TransferStructuredTableBarcodingStrategy,
  TransferStructuredTableDestinationType,
} from './TransferStructuredTableAdapter';

// Mapping from a cell value to the row in the table it comes from
interface RowIndexByCellValue {
  [value: string]: Array<number>;
}

/**
 * This function identifies which rows have duplicate cells and returns an array of CellPositionError error's
 * @param rowIndexByCellValue - mapping of a value to an array of row indices where the value can be found
 */
function _getErrorsForDuplicateValuesInRows({
  rowIndexByCellValue,
  columnIndex,
  errorToShow,
}: {
  rowIndexByCellValue: RowIndexByCellValue;
  columnIndex: number;
  errorToShow: string;
}): Array<CellErrorPosition> {
  const errors: Array<CellErrorPosition> = [];
  for (const inputString of Object.keys(rowIndexByCellValue)) {
    if (rowIndexByCellValue[inputString].length > 1) {
      for (const [i, rowIndex] of rowIndexByCellValue[inputString].entries()) {
        // Don't show error for first occurrence
        if (i === 0) {
          continue;
        }
        errors.push({
          rowIndex,
          columnIndex,
          error: {
            type: 'ERROR',
            message: errorToShow,
          },
        });
      }
    }
  }
  return errors;
}

export function getErrorsForDuplicateBarcodesInNonFinalizedRows({
  structuredTable,
  sourceTable,
}: {
  structuredTable: TransferStructuredTable;
  sourceTable: Table;
}): Array<CellErrorPosition> {
  // This check is only valid for custom barcode inputs
  if (
    structuredTable.metadata.mode === InventoryStructuredTableMode.FILL ||
    structuredTable.metadata.barcodingStrategy === TransferStructuredTableBarcodingStrategy.AUTOGENERATE
  ) {
    return [];
  }
  const columnDefinitions = sourceTable.columnConstraints.map((constraint) => constraint?.columnDefinition);
  const barcodeColumnIndex = getColumnDefinitionIndex({
    columnGroup: TransferTableColumnGroup.TARGET,
    tableColumnDefinitions: columnDefinitions,
    columnDefinitionType: TableColumnType.CONTAINERS,
  });

  const rowIndexByBarcode: RowIndexByCellValue = {};

  for (const [rowIndex, row] of structuredTable.rows.entries()) {
    if (row.isFinalized) {
      continue;
    }
    const value = row.cells[barcodeColumnIndex].text;
    if (value === '') {
      continue;
    }
    if (rowIndexByBarcode[value]) {
      rowIndexByBarcode[value].push(rowIndex);
    } else {
      rowIndexByBarcode[value] = [rowIndex];
    }
  }

  return _getErrorsForDuplicateValuesInRows({
    rowIndexByCellValue: rowIndexByBarcode,
    columnIndex: barcodeColumnIndex,
    errorToShow: BARCODE_ALREADY_ENTERED_ERROR,
  });
}

// This map lets us find duplicate rows given a grid ID and a position string.
interface RowIndexByGridPositionByGrid {
  [gridId: string]: {
    // If the array has more than one row index, there's a duplicate position in the table.
    [positionString: string]: Array<number>;
  };
}

export function getErrorsForDuplicatePositionsInNonFinalizedRows({
  structuredTable,
  computedTable,
  sourceTable,
}: {
  structuredTable: TransferStructuredTable;
  computedTable: Array<Array<EvaluatedCell>>;
  sourceTable: Table;
}): Array<CellErrorPosition> {
  const destinationType = structuredTable.metadata.destinationType;
  // This check is only valid for box / plate position columns
  if (
    destinationType !== TransferStructuredTableDestinationType.BOX &&
    destinationType !== TransferStructuredTableDestinationType.MATRIX_PLATE &&
    destinationType !== TransferStructuredTableDestinationType.PLATE
  ) {
    return [];
  }
  let storableColumnDefinitionType: TableColumnType;
  switch (destinationType) {
    case TransferStructuredTableDestinationType.BOX:
      storableColumnDefinitionType = TableColumnType.BOX;
      break;
    case TransferStructuredTableDestinationType.MATRIX_PLATE:
      storableColumnDefinitionType = TableColumnType.PLATE;
      break;
    case TransferStructuredTableDestinationType.PLATE:
      storableColumnDefinitionType = TableColumnType.PLATE;
      break;
    default:
      throw new Error(`Unexpected destination type ${assertNever(destinationType)}`);
  }

  const columnDefinitions = sourceTable.columnConstraints.map((constraint) => constraint?.columnDefinition);

  const boxOrPlateColumnIndex = getColumnDefinitionIndex({
    columnGroup: TransferTableColumnGroup.TARGET,
    tableColumnDefinitions: columnDefinitions,
    columnDefinitionType: storableColumnDefinitionType,
  });
  const positionColumnIndex = getColumnDefinitionIndex({
    columnGroup: TransferTableColumnGroup.TARGET,
    tableColumnDefinitions: columnDefinitions,
    columnDefinitionType: TableColumnType.POSITION,
  });

  const rowIndexByGridPositionByGrid: RowIndexByGridPositionByGrid = {};

  // For each unfinalized row, map each box/plate to its mapping of positions to the rows they come from
  for (const [rowIndex, row] of structuredTable.rows.entries()) {
    if (row.isFinalized) {
      continue;
    }

    const boxOrPlate = computedTable[rowIndex][boxOrPlateColumnIndex].value;

    if (boxOrPlate.type !== BaseTypedValueType.STORABLES || !boxOrPlate.storableSummaries.length) {
      // Box/plate name has not resolved to a chip yet; we can wait to validate until resolution occurs,
      // or no box/plate is present; so skip evaluation.
      continue;
    }
    const boxOrPlateId = boxOrPlate.storableSummaries[0].id;

    let positionString = row.cells[positionColumnIndex].text;
    if (positionString === '') {
      continue;
    }

    // Position is either alphanumeric or a 1-indexed integer
    // In order to check for conflicts, we need to convert an integer position to an alphanumeric position
    if (isIntegerPosition(positionString)) {
      const boxOrPlateDimensions = getGridDimensionsFromGQLCache(boxOrPlateId);

      if (boxOrPlateDimensions != null) {
        positionString = getAlphanumericPositionFromWellIndex(
          // positionString is 1-indexed, but getAlphanumericPositionFromWellIndex expects 0-indexed
          parseInt(positionString, 10) - 1,
          boxOrPlateDimensions
        );
      }
    }

    // Box/plate has not been entered in previous rows
    if (!rowIndexByGridPositionByGrid[boxOrPlateId]) {
      const rowIndexByGridPosition = {
        [positionString]: [rowIndex],
      };
      rowIndexByGridPositionByGrid[boxOrPlateId] = rowIndexByGridPosition;
    }
    // Box/plate has been entered in a previous row
    else {
      const rowIndexByPosition = rowIndexByGridPositionByGrid[boxOrPlateId];
      // Position has not been entered in previous rows for this box/plate
      if (!rowIndexByPosition[positionString]) {
        rowIndexByPosition[positionString] = [rowIndex];
      }
      // Position has been entered in a previous row for this box/plate
      else {
        rowIndexByPosition[positionString].push(rowIndex);
      }
    }
  }

  // For each plate, find if any positions have more than one occurrence
  let errors: Array<CellErrorPosition> = [];
  for (const boxOrPlate of Object.keys(rowIndexByGridPositionByGrid)) {
    const positionToRowIndex = rowIndexByGridPositionByGrid[boxOrPlate];
    errors = errors.concat(
      _getErrorsForDuplicateValuesInRows({
        rowIndexByCellValue: positionToRowIndex,
        columnIndex: positionColumnIndex,
        errorToShow: POSITION_ALREADY_ENTERED_ERROR,
      })
    );
  }

  return errors;
}

export function getErrorsForDuplicateDestinationChipsInNonFinalizedRows({
  structuredTable,
  computedTable,
  sourceTable,
}: {
  structuredTable: TransferStructuredTable;
  computedTable: Array<Array<EvaluatedCell>>;
  sourceTable: Table;
}): Array<CellErrorPosition> {
  if (
    structuredTable.metadata.mode !== InventoryStructuredTableMode.FILL ||
    structuredTable.metadata.destinationType !== TransferStructuredTableDestinationType.CONTAINER
  ) {
    return [];
  }

  const columnDefinitions = sourceTable.columnConstraints.map((constraint) => constraint?.columnDefinition);
  const destinationContainerColumnIndex = getColumnDefinitionIndex({
    columnGroup: TransferTableColumnGroup.TARGET,
    tableColumnDefinitions: columnDefinitions,
    columnDefinitionType: TableColumnType.CONTAINERS,
  });

  const rowIndexByContainerId: RowIndexByCellValue = {};
  for (const [rowIndex, row] of structuredTable.rows.entries()) {
    if (row.isFinalized) {
      continue;
    }
    const container = computedTable[rowIndex][destinationContainerColumnIndex].value;
    if (container.type !== BaseTypedValueType.STORABLES || container.storableSummaries.length === 0) {
      // Container has not resolved to a chip yet; we can wait to validate until resolution occurs.
      continue;
    }
    const containerId = container.storableSummaries[0].id;
    if (rowIndexByContainerId[containerId]) {
      rowIndexByContainerId[containerId].push(rowIndex);
    } else {
      rowIndexByContainerId[containerId] = [rowIndex];
    }
  }
  return _getErrorsForDuplicateValuesInRows({
    rowIndexByCellValue: rowIndexByContainerId,
    columnIndex: destinationContainerColumnIndex,
    errorToShow: CONTAINER_ALREADY_ENTERED_ERROR,
  });
}
